import { useEffect, useRef, useState } from "react";
import type { Shader } from "../types";
import { ShaderRunner } from "../lib/ShaderRunner";

type Props = {
  entry: Shader;
  onOpen: (s: Shader) => void;
};

export function EntryCard({ entry, onOpen }: Props) {
  const cardRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runnerRef = useRef<ShaderRunner | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !canvasRef.current) return;
    const runner = new ShaderRunner(canvasRef.current, entry.frag);
    runnerRef.current = runner;
    runner.start();
    return () => {
      runner.dispose();
      runnerRef.current = null;
    };
  }, [visible, entry]);

  return (
    <article
      ref={cardRef}
      className="card"
      onClick={() => onOpen(entry)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(entry);
        }
      }}
    >
      <div className="card__stage">
        <canvas ref={canvasRef} className="card__canvas" />
      </div>
      <div className="card__meta">
        <div className="card__week">w{String(entry.week).padStart(2, "0")}</div>
        <div className="card__body">
          <h3 className="card__title">{entry.title}</h3>
          <div className="card__sub">
            <span>{entry.date}</span>
            <span className="card__dot">·</span>
            <span>{entry.tags.join(" · ")}</span>
          </div>
        </div>
        <div className="card__chev">→</div>
      </div>
    </article>
  );
}
